<template>

    <div class="row h-100 pt-3">
        <div class="col h-100 d-flex flex-column users-column">
            <div class="np-header1 pb-4">{{ $t("UI.admin.users")}}</div>
            <div class="np-group np-group-column pt-2">
                <div class="np-group justify-content-between pb-3">
                    <button class="np-btn np-outline me-2">
                        <svg><use href="@/assets/images/icons/filter.svg#icon"></use></svg>
                    </button>
                    <button class="np-btn np-outline me-3">
                        <svg><use href="@/assets/images/icons/sorting.svg#icon"></use></svg>
                    </button>
                    <div class="np-field-group np-with-icon-right me-2">
                        <input name="search-field" type="text" class="np-text-input np-outline" :placeholder="$t('search.search')" v-model="filters.name" />
                        <svg class="field-icon at-left"><use href="@/assets/images/icons/search.svg#icon"></use></svg>
                    </div>
                    <button class="np-btn np-btn-sm np-light-gray np-p-custom-0" @click="callAddEmployee">
                        <svg><use href="@/assets/images/icons/plus.svg#icon"></use></svg>
                    </button> 
                </div>          
            </div>
        
            <div class="np-group np-group-column np-mt-3 np-flex-1">
                <PerfectScrollbar>
                    <ul class="np-list">
                        <li role="button" v-for="em in employees" :key="em.id" @click="doSelectEmployer(em)" :class="{'active' : em.id == currentEmployee?.id}"> 
                            <div class="np-group align-items-center p-2">
                                <CobaltProfile 
                                    :firstName="em.user_data.first_name" 
                                    :lastName="em.user_data.last_name" 
                                    :avatar="em.user_data.imageFile" 
                                    size="cobalt-profile icon"
                                    short
                                />                                   
                                <div class="np-group np-group-column ps-3">
                                    <div class="np-header3 pb-1">{{ em.user_data.first_name }} {{ em.user_data.last_name }}</div>
                                    <div class="np-subheader3">{{ em.position.name }}</div>
                                </div>
                            </div>            
                        </li>                                                                                                                                                                                                                                                                                                                               
                    </ul>
                </PerfectScrollbar>            
            </div>
            <div class="np-group np-border-top np-pt-4">
                <button class="np-btn np-btn-sm np-outline w-100">
                    <svg class="icon"><use href="@/assets/images/icons/table.svg#icon"></use></svg>
                    <span class="text-start np-flex-1">{{ $t("UI.employee.uploadXls") }}</span>
                    <svg><use href="@/assets/images/icons/plus.svg#icon"></use></svg>
                </button>
            </div>                                        
        </div>

        <div class="col d-flex flex-column h-100 pt-4">
            <div class="row pb-4" v-if="currentEmployee?.id">
                <div class="col-8">
                    <div class="np-panel h-100">
                        <div class="np-panel-body np-p-3">
                            <div class="np-group">
                                <div class="np-group np-group-column np-flex-1 np-shrink-0 pe-3" style="min-width: 60%;">
                                    <div class="np-group np-mb-2">
                                        
                                        <CobaltProfile 
                                            :firstName="currentEmployee.user_data.first_name" 
                                            :lastName="currentEmployee.user_data.last_name" 
                                            :avatar="currentEmployee.user_data.imageFile" 
                                            size="cobalt-profile team-member__avatar np-m-0 me-1"
                                            short
                                        />                                        

                                        <div class="np-group np-group-column align-items-start p-2 ps-4">
                                            <h2 class="np-header2v">{{ currentEmployee.user_data.first_name }} {{ currentEmployee.user_data.last_name }}</h2>
                                            <span class="np-badge-s np-outline mt-2 mb-3">{{ currentEmployee.position.name }}</span>
                                            <h3 class="np-header3 pt-3 pb-2">{{ $t("UI.organization.department") }}</h3>
                                            <div class="np-group np-group-column justify-content-start">
                                                <span class="vertical-align-center">{{ this.getDepartment(currentEmployee.department_id).title }}</span>
                                            </div> 
                                        </div>    
                                    </div>
                                    <div class="np-group np-group-column">
                                        <h3 class="np-header3 pt-2 pb-2">{{ $t("UI.usedSpace") }}</h3>
                                        <span class="np-pb-1">75% из {{ formatBytes(currentEmployee.limit_space_file * 1024 * 1024) }}</span>
                                        <ProgressBar 
                                            :min="0" 
                                            :max="currentEmployee.limit_space_file" 
                                            :current="0"
                                        />                                   
                                    </div>
                                    <div class="np-group np-group-column align-items-start mt-4">
                                        <button class="np-btn np-btn-sm np-outline" :disabled="isRegenerationStarted" @click="doRegenerateAuthData">
                                            <span class="icon spinner-border spinner-border-np me-2" role="status" aria-hidden="true" v-if="isRegenerationStarted"></span>
                                            <svg class="icon" v-else><use href="@/assets/images/icons/key.svg#icon"></use></svg>
                                            <span class="text-start np-flex-1">{{ $t("UI.employee.generateAuthData") }}</span>
                                        </button>                                               
                                    </div>           
                                </div>
                                <div class="np-group np-group-column justify-content-start np-flex-1 ps-3">
                                    <div class="np-group justify-content-end np-pb-4">                                                                                                                  
                                        <button class="np-btn np-btn-sm np-outline np-mr-2" @click="callEditEmployee">
                                            <svg class="icon"><use href="@/assets/images/icons/edit.svg#icon"></use></svg>
                                            <span class="text-start np-flex-1">{{ $t("UI.edit") }}</span>
                                        </button> 
                                        <button class="np-btn np-btn-sm np-outline">
                                            <svg><use href="@/assets/images/icons/dots-vertical.svg#icon"></use></svg>
                                        </button> 
                                    </div>
                                    <h3 class="np-header3 pb-3">{{ $t("UI.employee.contacts") }}</h3>
                                    <div class="np-group np-group-column justify-content-start np-pt-2 np-pb-4">
                                        <a class="vertical-align-center np-pb-1" href="#">
                                            <svg class="icon"><use href="@/assets/images/icons/message.svg#icon"></use></svg> {{ currentEmployee.user_data.mail }}
                                        </a>
                                        <a class="vertical-align-center np-pb-1" href="#">
                                            <svg class="icon"><use href="@/assets/images/icons/phone.svg#icon"></use></svg> {{ currentEmployee.user_data.phone }}
                                        </a>
                                    </div>  
                                    <h3 class="np-header3 pt-2 pb-3">{{ $t("UI.employee.additionalInfo") }}</h3>
                                    <div class="np-group np-group-column justify-content-start pt-3">
                                        <!--
                                        <span class="vertical-align-center np-pb-1" href="#">
                                            <svg class="icon"><use href="@/assets/images/icons/user-scan.svg#icon"></use></svg> ID938271
                                        </span>
                                        -->
                                        <span class="vertical-align-center pb-1" href="#">
                                            <svg class="icon"><use href="@/assets/images/icons/send.svg#icon"></use></svg> {{ currentEmployee.user_data.login }}
                                        </span>
                                        <span class="vertical-align-center" href="#">
                                            <svg class="icon"><use href="@/assets/images/icons/time.svg#icon"></use></svg> {{ $t("UI.employee.added") }}: {{ localeDate(currentEmployee.user_data.date_create) }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>              
                    </div>
                </div>
                <div class="col-4">
                    <div class="np-header2 vertical-align-center pb-2">
                        <svg class="np-mr-2"><use href="@/assets/images/icons/bug.svg#icon"></use></svg> Системные ошибки, сбои
                    </div>                    
                    <div class="np-panel">
                        <div class="np-panel-header justify-content-between np-p-2">
                            <select class="np-select"><option>Сортировать по: Алфавиту</option></select>
                            <button class="np-btn np-btn-primary np-p-custom-0">
                                <svg><use href="@/assets/images/icons/filter.svg#icon"></use></svg>
                            </button>
                        </div>
                        <div class="np-panel-body np-p-0">
                            <ul class="np-list">
                                <li class="no-wrap">
                                    <div class="np-group np-p-custom-2 align-items-center">
                                        <span class="no-wrap">Главный дашборд</span>
                                    </div>
                                </li>
                                <li class="no-wrap">
                                    <div class="np-group np-p-custom-2 align-items-center">
                                        <span class="no-wrap">Иконки для программы</span>
                                    </div>
                                </li>
                                <li class="no-wrap">
                                    <div class="np-group np-p-custom-2 align-items-center">
                                        <span class="no-wrap">Дизайн сайта</span>
                                    </div>
                                </li>
                                <li class="no-wrap">
                                    <div class="np-group np-p-custom-2 align-items-center">
                                        <span class="no-wrap">Разработка системы с очень, очень длинным названием</span>
                                    </div>
                                </li>                               
                            </ul>
                        </div>
                    </div>
                </div>                        
            </div>
            <div class="row flex-grow-1 pt-4" v-if="currentEmployee?.id">
                <div class="col-6 h-100">                           
                    <div class="np-header2 vertical-align-center pb-2">
                        <svg class="me-2"><use href="@/assets/images/icons/setting.svg#icon"></use></svg> Права доступа к функционалу программы
                    </div>  
                    <div class="np-panel mb-3">
                        <div class="np-panel-header justify-content-between align-items-end p-3 np-borderless">
                            <h3 class="np-header3">Контакты</h3>
                            <button class="np-btn np-btn-sm np-outline">
                                <svg class="icon"><use href="@/assets/images/icons/edit.svg#icon"></use></svg>
                                <span class="text-start np-flex-1">Редактировать</span>
                            </button>                                    
                        </div>
                        <div class="np-panel-body p-3 pt-0">
                            <div class="np-group np-flex-wrap">
                                <div class="np-badge-s np-inactive mt-2 me-2">Testo</div>
                                <div class="np-badge-s np-inactive mt-2 me-2">Это проект с весьма длинным названием, весьма</div>
                                <div class="np-badge-s np-inactive mt-2 me-2">Это проект с весьма весьма</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-6 h-100">
                    <div class="np-header2 vertical-align-center pb-2">
                        <svg class="me-2"><use href="@/assets/images/icons/folder-open.svg#icon"></use></svg> Доступ к проектам
                    </div>  
                    <div class="np-panel">
                        <div class="np-panel-body p-0">
                            <ul class="np-list">
                                <li class="no-wrap">
                                    <div class="np-group np-p-custom-2 align-items-center">
                                        <span class="no-wrap">Главный дашборд</span>
                                    </div>
                                </li>
                                <li class="no-wrap">
                                    <div class="np-group np-p-custom-2 align-items-center">
                                        <span class="no-wrap">Иконки для программы</span>
                                    </div>
                                </li>
                                <li class="no-wrap">
                                    <div class="np-group np-p-custom-2 align-items-center">
                                        <span class="no-wrap">Дизайн сайта</span>
                                    </div>
                                </li>
                                <li class="no-wrap">
                                    <div class="np-group np-p-custom-2 align-items-center">
                                        <span class="no-wrap">Разработка системы с очень, очень длинным названием</span>
                                    </div>
                                </li>                               
                            </ul>
                        </div>
                    </div>
                </div>                        
            </div> 
            <div class="np-panel h-100 align-items-center justify-content-center" v-if="!!!currentEmployee?.id">
                {{ $t("UI.employee.selectOrCreateEmployee") }}
            </div>                   
        </div> 
    </div>

    <EmployeeModal modalID="modalEmpNewID"  @closeModal="doAfterClose" @afterSave="doAfterSave" @positionEdit="callPositionsEdit" ref="employeeModal" />
    <PositionModal modalID="inputPositionModalID" ref="positionModal" />
</template>

<script>
import { useOrganizationStore } from '@/stores/organizationStore'
import Config from '../../config'
import { localeDate, formatBytes } from '@/utils/format'
import Accordion from '@/components/elements/Accordion.vue'
import { createToast } from 'mosha-vue-toastify' 
import { DatePicker } from "qalendar"
import "qalendar/dist/style.css"
import EmployeeModal from '../modals/EmployeeModal.vue'
import PositionModal from '../modals/PositionsModal.vue'
import CobaltProfile from "@/components/dashboard/parts/CobaltProfile.vue"
import ProgressBar from '@/components/elements/ProgressBar.vue'

export default {
    components: {
        DatePicker,
        Accordion,
        CobaltProfile,
        ProgressBar,
        EmployeeModal,
        PositionModal
    },
    data() {
        return {
            Config,
            filters: {
                name: ''
            }, 
            currentEmployee: null,            
            selectedEmployee: {
                parent_id: null,
                department_id: null,
                position_id: null,
                is_staff: true,
                is_admin: false,
                login: "",
                first_name: "",
                last_name: "",
                patronymic: "",
                roles_id: [],
                mail: "",
                phone: "",
                password: "",
                limit_space_file: 0
            },
            isRegenerationStarted: false
        }
    },
    async beforeMount() {
        await this.organizationStore?.init()
    },
    computed: {
        organizationStore() {
            return useOrganizationStore()
        },
        employees() {
            const searchTerm = this.filters.name.trim().toLowerCase()
            const fieldsToSearch = ['first_name', 'last_name']

            if (this.organizationStore.employees) {

                const result = this.organizationStore.employees.filter(item => {
                    return fieldsToSearch.some(field => 
                        String(item.user_data[field]).toLowerCase().includes(searchTerm)
                    )
                })
                                    
                return result
            } else {
                return []
            }
        }                          
    },
    methods: {
        localeDate,
        formatBytes,
        getDepartment(id) {
            return this.organizationStore.department(id)
        },
        showEmployeeModal() {
            console.log(this.selectedEmployee)
            this.$refs.employeeModal.shows(this.selectedEmployee)
        },
        clearCurrentEmployee() {
            this.currentEmployee = null
        },
        clearSelectedEmployee() {
            this.selectedEmployee = {
                id: null,
                parent_id: null,
                department_id: null,
                position_id: null,
                is_staff: true,
                is_admin: false,
                login: "",
                first_name: "",
                last_name: "",
                patronymic: "",
                roles_id: [],
                mail: "",
                phone: "",
                password: "",
                limit_space_file: 0,
                imageFile: null
            }
        }, 
        doSelectEmployer(em) {
            this.currentEmployee = {...em}
        },
        async doRegenerateAuthData() {
            this.isRegenerationStarted = true

            const response = await this.organizationStore.regenerateAuthData(this.currentEmployee.id)

            if (response) {
                createToast({ description: this.$t("message.regenerateSuccess") }, Config.TOAST_CONFIG_SUCCESS) 
            } else {
                createToast({ description: this.$t("message.regenerateFailed") }, Config.TOAST_CONFIG_ERROR) 
            }

            this.isRegenerationStarted = false
        },
        callAddEmployee() {
            //this.clearCurrentEmployee()
            this.clearSelectedEmployee()
            this.showEmployeeModal()
        },   
        callEditEmployee() {
            this.selectedEmployee = {... this.currentEmployee.user_data}
            this.selectedEmployee.id = this.currentEmployee.id
            this.selectedEmployee.department_id = this.currentEmployee.department_id
            this.selectedEmployee.position_id = this.currentEmployee.position.id
            this.selectedEmployee.limit_space_file = this.currentEmployee.limit_space_file
            this.showEmployeeModal()
        },         
        callPositionsEdit(data) {
            this.$refs.positionModal.shows(data)
        },
        doAfterSave(emp) {
            this.doSelectEmployer(emp)
            createToast({ description: this.$t("message.saveSuccess") }, Config.TOAST_CONFIG_SUCCESS) 
        },
        doAfterClose(id) {
            if (id) {
                const emp = this.employees.find(e => e.id === id)
                this.doSelectEmployer(emp)
            }
        }
    }

}
</script>

<style scoped>
    .users-column {
        max-width: 340px;
    }
    .home {
        width: 100%;
        height: 100%;
    }
    .shelf {
        flex: 1 1 auto;
        width: 100%;
        margin-bottom: 10px;
        border: none;
        background-color: transparent;
        display: flex;
        flex-direction: row;
        font-size: 13px;
    }
    .shelf:last-child {
        margin-bottom: 0;
    }
    .labelbox {
        flex: 0 0 70%;
    }
    .valuebox {
        flex: 0 0 10%;
    }
    .valuebox img {
        width: 24px;
        opacity: .8;
    }
    .command-user__list-title.shelf {
        font-size: 14px;
        font-weight: 500;
    }
    .stats-select-date {
        padding: 10px 20px;
        font-size: 12px;
        background-color: #FFF;
        border: 10px;
        border-width: 0;
        box-shadow: 0 3px 5px rgb(0 0 0 / 4%);
        display: flex;
        align-items: center;
        justify-content: center;
    }
</style>